import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shield, Eye, LogIn, Building2, Users, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function PlatformTenantsPage() {
  const { isPlatformAdmin, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [mode, setMode] = useState("read_only");
  const [duration, setDuration] = useState(15);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);

  useEffect(() => {
    if (!isPlatformAdmin) { navigate("/"); return; }
    api.get("/platform/tenants").then((r) => setData(r.data)).catch(() => setData({ tenants: [], total: 0 }));
    api.get("/platform/impersonation-log?limit=50").then((r) => setLog(r.data.log));
  }, [isPlatformAdmin, navigate]);

  if (!isPlatformAdmin) return null;
  if (!data) return <div className="text-neutral-500" data-testid="platform-loading">Lade Mandanten…</div>;

  const filtered = data.tenants.filter((t) => {
    const s = (q || "").toLowerCase();
    if (!s) return true;
    return (t.tenant.name || "").toLowerCase().includes(s)
      || ((t.owner?.email) || "").toLowerCase().includes(s)
      || ((t.owner?.name) || "").toLowerCase().includes(s);
  });

  const doImpersonate = async () => {
    if (!confirming) return;
    setBusy(true);
    try {
      await api.post("/platform/impersonate", {
        tenant_id: confirming.tenant.id,
        mode, duration_minutes: Number(duration),
      });
      toast.success(`Eingeloggt als ${confirming.tenant.name} (${mode})`);
      setConfirming(null);
      await refresh();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="platform-tenants-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold flex items-center gap-2">
            <Shield className="h-3.5 w-3.5" /> Platform-Admin
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Alle Mandanten</h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            Dieses Panel ist nur für Platform-Admins sichtbar. Impersonation wird protokolliert (verdeckt).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowLog(true)} data-testid="platform-log-btn">
          <Eye className="h-4 w-4 mr-2" /> Impersonation-Log
        </Button>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-4">
        <div className="flex items-center gap-3 mb-4">
          <Search className="h-4 w-4 text-neutral-500" />
          <Input placeholder="Nach Firma, Name oder E-Mail suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="platform-search" />
          <div className="text-sm text-neutral-500 whitespace-nowrap">{filtered.length} / {data.total}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="text-left py-3 font-semibold">Mandant</th>
                <th className="text-left py-3 font-semibold">Owner</th>
                <th className="text-left py-3 font-semibold">Angelegt</th>
                <th className="text-right py-3 font-semibold">Kunden</th>
                <th className="text-right py-3 font-semibold">Rechnungen</th>
                <th className="text-right py-3 font-semibold">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, idx) => (
                <tr key={t.tenant.id} className="border-b border-neutral-100 hover:bg-neutral-50/50" data-testid={`platform-row-${idx}`}>
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-neutral-400" />
                      <span className="font-medium">{t.tenant.name}</span>
                    </div>
                    <div className="text-xs text-neutral-400 mt-0.5 font-mono">{t.tenant.id.slice(0, 8)}…</div>
                  </td>
                  <td className="py-3">
                    <div>{t.owner?.name || "—"}</div>
                    <div className="text-xs text-neutral-500">{t.owner?.email || "—"}</div>
                  </td>
                  <td className="py-3 text-xs text-neutral-600">{fmtDate(t.tenant.created_at)}</td>
                  <td className="py-3 text-right tabular-nums">{t.counts.customers}</td>
                  <td className="py-3 text-right tabular-nums">{t.counts.outgoing_invoices + t.counts.incoming_invoices}</td>
                  <td className="py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => { setConfirming(t); setMode("read_only"); setDuration(15); }} data-testid={`platform-impersonate-${idx}`}>
                      <LogIn className="h-3.5 w-3.5 mr-1.5" /> Einloggen
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-neutral-500">Keine Treffer.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm dialog */}
      <Dialog open={!!confirming} onOpenChange={(v) => { if (!v) setConfirming(null); }}>
        <DialogContent data-testid="platform-confirm-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="h-4 w-4 text-red-600" /> Als Mandant einloggen?</DialogTitle></DialogHeader>
          {confirming && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg bg-neutral-50 border p-3">
                <div><strong>Mandant:</strong> {confirming.tenant.name}</div>
                <div><strong>Owner:</strong> {confirming.owner?.name} ({confirming.owner?.email})</div>
              </div>
              <div>
                <label className="text-xs text-neutral-600 mb-1 block">Modus</label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="platform-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read_only">Read-Only (nur ansehen, empfohlen)</SelectItem>
                    <SelectItem value="full">Full Access (ändern erlaubt)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-neutral-600 mb-1 block">Dauer (Minuten)</label>
                <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 Minuten</SelectItem>
                    <SelectItem value="30">30 Minuten</SelectItem>
                    <SelectItem value="60">1 Stunde</SelectItem>
                    <SelectItem value="120">2 Stunden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirming(null)}>Abbrechen</Button>
            <Button onClick={doImpersonate} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="platform-confirm-btn">
              {busy ? "Melde an…" : "Einloggen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log dialog */}
      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> Impersonation-Log (letzte 50)</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2 text-xs">
            {log.length === 0 && <div className="text-neutral-500">Keine Einträge.</div>}
            {log.map((l, i) => (
              <div key={i} className="rounded border border-neutral-200 p-2 flex flex-wrap justify-between gap-2">
                <div>
                  <div className="font-medium">{l.real_email} → {l.target_tenant_name}</div>
                  <div className="text-neutral-500">Modus: {l.mode} · {l.duration_minutes} Min</div>
                </div>
                <div className="text-neutral-500 text-right">
                  <div>Start: {l.started_at?.slice(0, 19).replace("T", " ")}</div>
                  <div>{l.ended_at ? `Ende: ${l.ended_at.slice(0, 19).replace("T", " ")}` : "Noch aktiv"}</div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
